// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { Box, Button, Grid2, IconButton, Stack, Typography } from '@mui/material';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TFunction } from 'i18next';
import moment from 'moment/moment';
// Components, Layouts, Pages
import { BaseDatePicker, BaseLabel, BaseSelect, BaseTable, Loading, UnderDevelopment } from '~/components/common';
// Others
import { IOverbookedItemReport, IQueryParamListExport } from '~/utils/interface/reports';
import {
  BACK_TO_PREVIOUS_PAGE,
  DEFAULT_CURRENT_PAGE,
  DEFAULT_LIMIT_PAGE,
  EMPTY_VALUE,
  optionGroupBy,
  optionTransactionType,
} from '~/utils/constants/common';
import { IPaginationResponse } from '~/utils/interface/base';
import { IBaseOption, IHeaderCellType, IResultCallApiFn } from '~/utils/interface/common';
import { DateFormatEnum, GroupByEnum, ReportTypeEnum, TypeInventoryGroupDay } from '~/utils/enum/common';
import { formatDate, formatTimeTo12h, getFullName } from '~/utils/helper/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { exportDataReport, getListOverbookingItemReport } from '~/thunks/reports/reportsThunk';
// Styles, images, icons
import styles from './ReportsOverbookingItem.module.scss';
import { icons } from '~/assets';

const cx = classNames.bind(styles);

const columns = (t: TFunction): IHeaderCellType<IOverbookedItemReport>[] => {
  return [
    {
      id: 'orderNumber',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_order_number')}</Box>
        </Stack>
      ),
      render: (record: IOverbookedItemReport) => (
        <Stack>
          <Typography className={cx('orderNumber')}>{record?.orderNumber}</Typography>
        </Stack>
      ),
    },
    {
      id: 'title',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_title')}</Box>
        </Stack>
      ),
      render: (record: IOverbookedItemReport) => {
        return record?.title;
      },
    },
    {
      id: 'createdAt',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_original_date')}</Box>
        </Stack>
      ),
      render: (record: IOverbookedItemReport) => {
        return formatDate(record?.createdAt, DateFormatEnum.M_D_YYYY);
      },
    },
    {
      id: 'updatedAt',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_last_updated')}</Box>
        </Stack>
      ),
      render: (record: IOverbookedItemReport) => {
        return formatDate(record?.updatedAt, DateFormatEnum.M_D_YYYY);
      },
    },
    {
      id: 'client',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_client')}</Box>
        </Stack>
      ),
      render: (record: IOverbookedItemReport) => {
        return getFullName({ firstName: record?.client?.firstName, lastName: record?.client?.lastName });
      },
    },
    {
      id: 'startDate',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_order_date')}</Box>
        </Stack>
      ),
      render: (record: IOverbookedItemReport) => {
        return formatDate(record?.startDate, DateFormatEnum.M_D_YYYY);
      },
    },
    {
      id: 'startTime',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_event_time')}</Box>
        </Stack>
      ),
      render: (record: IOverbookedItemReport) => {
        return (
          <Typography className={cx('textTable')}>
            {`${formatDate(record.startDate, DateFormatEnum.MMM_D_YYYY)} ${formatTimeTo12h(record?.startTime)} - ${formatDate(record.endDate ?? record.startDate, DateFormatEnum.MMM_D_YYYY)} ${formatTimeTo12h(record?.endTime ?? record?.startTime)}`}
          </Typography>
        );
      },
      width: '9%',
    },
    {
      id: 'eventType',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_type')}</Box>
        </Stack>
      ),
      render: (record: IOverbookedItemReport) => {
        return record?.eventType?.name ? <Box className={cx('typeView')}>{record?.eventType?.name}</Box> : EMPTY_VALUE;
      },
    },
    {
      id: 'productName',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_product')}</Box>
        </Stack>
      ),
      render: (record: IOverbookedItemReport) => (
        <Stack direction={'column'} spacing={1}>
          <Typography>{record?.productName}</Typography>
          <Typography className={cx('variantName')}>{record?.variantName}</Typography>
        </Stack>
      ),
    },
    {
      id: 'quantity',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_ordered_qty')}</Box>
        </Stack>
      ),
      render: (record: IOverbookedItemReport) => {
        return (
          <Stack direction={'row'} justifyContent={'center'}>
            <Typography className={cx('textQty')}>{record?.quantity}</Typography>
          </Stack>
        );
      },
    },
    {
      id: 'overbookedQuantity',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_overbooked_qty')}</Box>
        </Stack>
      ),
      render: (record: IOverbookedItemReport) => {
        return (
          <Stack direction={'row'} justifyContent={'center'}>
            <Typography className={cx('textOverbooking')}>{record?.overbookedQuantity}</Typography>
          </Stack>
        );
      },
    },
    {
      id: 'subRentalQuantity',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_resolved_qty')}</Box>
        </Stack>
      ),
      render: (record: IOverbookedItemReport) => {
        return (
          <Stack direction={'row'} justifyContent={'center'}>
            <Typography
              className={cx(
                record?.overbookedQuantity !== record?.subRentalQuantity ? 'textOverbooking' : 'textSubrental'
              )}
            >
              {record?.subRentalQuantity}
            </Typography>
          </Stack>
        );
      },
    },
  ];
};

const columnsProduct = (t: TFunction): IHeaderCellType<IOverbookedItemReport>[] => {
  return [
    {
      id: 'productName',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_product')}</Box>
        </Stack>
      ),
      render: (record: IOverbookedItemReport) => (
        <Stack direction={'column'} spacing={1}>
          <Typography>{record?.productName}</Typography>
          <Typography className={cx('variantName')}>{record?.variantName}</Typography>
        </Stack>
      ),
    },
    {
      id: 'quantity',
      label: (
        <Stack direction={'row'} alignItems={'start'} gap={2}>
          <Box>{t('report_table_ordered_qty')}</Box>
        </Stack>
      ),
      render: (record: IOverbookedItemReport) => {
        return (
          <Stack direction={'row'} justifyContent={'start'}>
            <Typography className={cx('textQty')}>{record?.quantity}</Typography>
          </Stack>
        );
      },
    },
    {
      id: 'overbookedQuantity',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_overbooked_qty')}</Box>
        </Stack>
      ),
      render: (record: IOverbookedItemReport) => {
        return (
          <Stack direction={'row'} justifyContent={'start'}>
            <Typography className={cx('textOverbooking')}>{record?.overbookedQuantity}</Typography>
          </Stack>
        );
      },
    },
    {
      id: 'subRentalQuantity',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_resolved_qty')}</Box>
        </Stack>
      ),
      render: (record: IOverbookedItemReport) => {
        return (
          <Stack direction={'row'} justifyContent={'start'}>
            <Typography
              className={cx(
                record?.overbookedQuantity !== record?.subRentalQuantity ? 'textOverbooking' : 'textSubrental'
              )}
            >
              {record?.subRentalQuantity}
            </Typography>
          </Stack>
        );
      },
    },
  ];
};

const ReportsOverbookingItem = () => {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/reports', 'common/base']);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(() => Number(params?.page ?? DEFAULT_CURRENT_PAGE), [params?.page]);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [overbookingItemList, setOverbookingItemList] = useState<IOverbookedItemReport[]>([]);
  const [pagination, setPagination] = useState<IPaginationResponse>();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [paramsObject, setParamsObject] = useState<IQueryParamListExport>({
    page: DEFAULT_CURRENT_PAGE,
    limit: DEFAULT_LIMIT_PAGE,
    transactionType: TypeInventoryGroupDay.ALL,
    startDate: moment().format(DateFormatEnum.YYYY_MM_DD),
    endDate: moment().format(DateFormatEnum.YYYY_MM_DD),
  });
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  const [isOrder, setIsOrder] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListOverbookingItem();
  }, [paramsObject.page]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListOverbookingItem = () => {
    setIsLoading(true);

    dispatch(getListOverbookingItemReport(paramsObject))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        const { responses, pagination } = res.data;

        setOverbookingItemList(responses);
        setPagination(pagination);

        if (paramsObject?.groupBy === GroupByEnum.ORDER) {
          setIsOrder(true);
        }

        if (paramsObject?.groupBy === GroupByEnum.PRODUCT) {
          setIsOrder(false);
        }
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const handleApplyFilter = () => {
    handleGetListOverbookingItem();
  };

  const handleExport = () => {
    setIsLoading(true);

    const params: IQueryParamListExport = {
      ...paramsObject,
      type: ReportTypeEnum.OVERBOOKING_ITEM,
    };

    dispatch(exportDataReport(params))
      .unwrap()
      .then((res) => {
        const { data } = res;

        handleDownloadFile(data?.url);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const handleDownloadFile = (fileUrl: string) => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileUrl;
    link.click();
  };

  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamsObject({ ...paramsObject, page });
    setSearchParams({ ...params, page: String(page) });
  };

  const handleBack = () => {
    navigate(BACK_TO_PREVIOUS_PAGE);
  };

  const fetchOptionGroupBy = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionGroupBy,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const fetchOptionTransactionType = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionTransactionType,
      hasMoreResp: false,
    };

    return result;
  }, []);
  //#endregion Handle Function

  return (
    <Stack id='reportsOverbookingItemPage' className={cx('container')} spacing={6}>
      <Stack direction='row' spacing={2} alignItems='center'>
        <IconButton color='primary' onClick={handleBack} className={cx('buttonBack')}>
          <img src={icons.commonIconBack} alt={t('common_alt_img', { ns: 'common/base' })} />
        </IconButton>

        <Stack direction={'column'} spacing={2}>
          <BaseLabel label={t('reports_overbooking_items_title')} styleType='bigTitle' />
          <Typography className={cx('description')}>{t('reports_overbooking_items_desc_page')}</Typography>
        </Stack>
      </Stack>

      <Stack direction={'column'} gap={5} className={cx('viewAction')}>
        <Grid2 columnSpacing={'24px'} alignItems={'end'} rowSpacing={'24px'} container>
          <Grid2 size={3}>
            <BaseDatePicker
              mode='date'
              height={40}
              label={t('common_text_start_date', { ns: 'common/base' })}
              value={paramsObject.startDate}
              onChange={(date) => {
                setParamsObject((prevState) => ({
                  ...prevState,
                  startDate: date as string,
                }));
              }}
            />
          </Grid2>

          <Grid2 size={3}>
            <BaseDatePicker
              mode='date'
              height={40}
              label={t('common_text_end_date', { ns: 'common/base' })}
              value={paramsObject.endDate}
              onChange={(date) => {
                setParamsObject((prevState) => ({
                  ...prevState,
                  endDate: date as string,
                }));
              }}
            />
          </Grid2>

          <Grid2 size={3}>
            <BaseSelect
              gap={8}
              height={40}
              styleType='contained'
              label={t('reports_filter_transaction_type')}
              placeholder={t('reports_filter_all_status')}
              onChange={(data) => {
                setParamsObject((prevState) => ({
                  ...prevState,
                  transactionType: data?.value,
                }));
              }}
              callApiFn={fetchOptionTransactionType}
            />
          </Grid2>

          <Grid2 size={3}>
            <BaseSelect
              gap={8}
              height={40}
              styleType='contained'
              label={t('reports_filter_group_by')}
              onChange={(data) => {
                setParamsObject((prevState) => ({
                  ...prevState,
                  groupBy: data?.value,
                }));
              }}
              callApiFn={fetchOptionGroupBy}
            />
          </Grid2>
        </Grid2>

        <Stack direction='row' justifyContent={'space-between'} alignItems='center'>
          <Button
            type='button'
            variant='contained'
            className={cx('buttonApply')}
            onClick={handleApplyFilter}
            startIcon={<img src={icons.commonIconFilter} alt={t('common_alt_img', { ns: 'common/base' })} />}
          >
            {t('common_action_apply', { ns: 'common/base' })}
          </Button>

          <Button
            type='button'
            variant='contained'
            className={cx('buttonExport')}
            onClick={handleExport}
            startIcon={<img src={icons.commonIconExportExcel} alt={t('common_alt_img', { ns: 'common/base' })} />}
          >
            {t('common_button_export_excel', { ns: 'common/base' })}
          </Button>
        </Stack>
      </Stack>

      <Box className={cx('tableContainer')}>
        <BaseTable
          dataSource={overbookingItemList}
          currentPage={pageSelected}
          totalPage={pagination?.totalPages}
          columns={isOrder ? columns(t) : columnsProduct(t)}
          onPageChange={handlePageChange}
        />
      </Box>

      {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}

      {isLoading && <Loading />}
    </Stack>
  );
};

export default ReportsOverbookingItem;
